import { formatNZDate, formatNZDateTime } from "../../utils/timezone";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import DatePicker from "../../components/DatePicker";
import { DollarSign, Eye, Edit, Trash2, Lock } from "lucide-react";

export function ShopQuoteViewDialog({
  viewQuoteDialogOpen,
  setViewQuoteDialogOpen,
  viewingQuote,
  setViewingQuote,
  editQuoteMode,
  setEditQuoteMode,
  quoteForm,
  setQuoteForm,
  savingQuoteEdit,
  handleSaveQuoteEdit,
  handleDeleteQuote,
  isQuoteEditable,
  isQuoteDeletable,
  getQuoteEditTimeRemaining,
  deletingQuote,
  onRequestDeletion,
  resetQuoteForm,
  formatDateForApi,
}) {
  return (
    <>
      <Dialog open={viewQuoteDialogOpen} onOpenChange={(open) => {
        setViewQuoteDialogOpen(open);
        if (!open) {
          setEditQuoteMode(false);
          setViewingQuote(null);
          resetQuoteForm();
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 flex-wrap">
              <span>Quote {viewingQuote?.quote_number || `#${viewingQuote?.quote_id?.substring(0, 8)}`}</span>
              {viewingQuote?.deletion_status === "pending" && (
                <Badge className="bg-red-500/10 text-red-600 border-red-500/30">
                  Deletion Requested
                </Badge>
              )}
              {viewingQuote?.deletion_status === "rejected" && (
                <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/30">
                  Deletion Declined
                </Badge>
              )}
              {viewingQuote && !viewingQuote.deletion_status && isQuoteEditable(viewingQuote.created_at) ? (
                <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                  <Edit className="h-3 w-3 mr-1" />
                  Editable ({getQuoteEditTimeRemaining(viewingQuote?.created_at)}min)
                </Badge>
              ) : viewingQuote && !viewingQuote.deletion_status && (
                <Badge className="bg-gray-500/10 text-gray-600 border-gray-500/30">
                  <Lock className="h-3 w-3 mr-1" />
                  View Only
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              For Request {viewingQuote?.request_number || `#${viewingQuote?.request_id}`} • {viewingQuote?.request_info?.service_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Service'}
            </DialogDescription>
          </DialogHeader>

          {/* Customer vehicle context strip — same data as the submit-quote
              dialog so shops can identify the car while reviewing/editing. */}
          {viewingQuote?.request_info?.vehicle_info && (() => {
            const v = viewingQuote.request_info.vehicle_info;
            const vehLine = [v.year, v.make, v.model].filter(Boolean).join(" ");
            if (!v.rego && !vehLine) return null;
            return (
              <div
                className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs border rounded-md bg-muted/30 px-3 py-2"
                data-testid="view-quote-vehicle-strip"
              >
                {v.rego && (
                  <span className="inline-flex items-center gap-1">
                    <span className="text-muted-foreground">Rego</span>
                    <span className="font-semibold tracking-wide uppercase">{v.rego}</span>
                  </span>
                )}
                {vehLine && (
                  <span className="inline-flex items-center gap-1">
                    <span className="text-muted-foreground">Vehicle</span>
                    <span className="font-medium">{vehLine}</span>
                    {v.sub_model && (
                      <span className="text-muted-foreground"> · {v.sub_model}</span>
                    )}
                  </span>
                )}
                {(v.body_type || v.fuel_type) && (
                  <span className="text-muted-foreground">
                    {[v.body_type, v.fuel_type].filter(Boolean).join(" · ")}
                  </span>
                )}
                {v.lookup_source === "carjam" && (
                  <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider bg-green-500/15 text-green-700 dark:text-green-300 border border-green-500/40">
                    CARJAM VERIFIED
                  </span>
                )}
              </div>
            );
          })()}

          {viewingQuote && (
            <div className="space-y-4">
              {editQuoteMode ? (
                // Edit Mode Form - matches submission form
                <div className="space-y-4">
                  {/* Tyre Details - only show if tyre service */}
                  {(viewingQuote.tyre_brand || viewingQuote.tyre_model || viewingQuote.request_info?.service_type?.includes("tyres")) && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Tyre Brand</Label>
                          <Input
                            placeholder="e.g. Michelin"
                            value={quoteForm.tyre_brand}
                            onChange={(e) => setQuoteForm({ ...quoteForm, tyre_brand: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Tyre Model</Label>
                          <Input
                            placeholder="e.g. Pilot Sport 5"
                            value={quoteForm.tyre_model}
                            onChange={(e) => setQuoteForm({ ...quoteForm, tyre_model: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Tyre Size</Label>
                        <Input
                          placeholder="e.g. 205/55R16"
                          value={quoteForm.tyre_size}
                          onChange={(e) => setQuoteForm({ ...quoteForm, tyre_size: e.target.value })}
                        />
                      </div>
                    </>
                  )}

                  {/* Price Breakdown Section - matches submission form */}
                  <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold">Pricing</Label>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="edit_prices_include_gst"
                          checked={quoteForm.prices_include_gst}
                          onCheckedChange={(checked) => setQuoteForm({ ...quoteForm, prices_include_gst: checked })}
                        />
                        <Label htmlFor="edit_prices_include_gst" className="text-sm cursor-pointer">
                          Prices include GST
                        </Label>
                      </div>
                    </div>
                    
                    <p className="text-xs text-muted-foreground">
                      {quoteForm.prices_include_gst 
                        ? "Enter your prices including GST - we'll calculate the breakdown" 
                        : "Enter your prices excluding GST - GST (15%) will be added automatically"}
                    </p>
                    
                    <div className="grid grid-cols-1 gap-3">
                      {/* Tyre & Fitment Cost - show if tyre service */}
                      {(viewingQuote.tyre_brand || viewingQuote.request_info?.service_type?.includes("tyres")) && (
                        <div className="space-y-1">
                          <Label className="text-sm">
                            Tyre & Fitment Cost {quoteForm.prices_include_gst ? "(incl. GST)" : "(excl. GST)"}
                          </Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Enter amount"
                            value={quoteForm.tyre_fitment_cost}
                            onChange={(e) => setQuoteForm({ ...quoteForm, tyre_fitment_cost: e.target.value })}
                          />
                        </div>
                      )}
                      
                      {/* Alignment Cost */}
                      {(viewingQuote.price_breakdown?.alignment || viewingQuote.request_info?.service_type?.includes("alignment")) && (
                        <div className="space-y-1">
                          <Label className="text-sm">
                            Wheel Alignment Cost {quoteForm.prices_include_gst ? "(incl. GST)" : "(excl. GST)"}
                          </Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Enter amount"
                            value={quoteForm.alignment_cost}
                            onChange={(e) => setQuoteForm({ ...quoteForm, alignment_cost: e.target.value })}
                          />
                        </div>
                      )}

                      {/* Rotation Cost - for rotation services */}
                      {(viewingQuote.request_info?.service_type?.includes("rotation") || viewingQuote.request_info?.selected_services?.includes("rotation")) && (
                        <div className="space-y-1">
                          <Label className="text-sm">
                            Tyre Rotation Cost {quoteForm.prices_include_gst ? "(incl. GST)" : "(excl. GST)"}
                          </Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Enter amount"
                            value={quoteForm.rotation_cost}
                            onChange={(e) => setQuoteForm({ ...quoteForm, rotation_cost: e.target.value })}
                          />
                        </div>
                      )}
                    </div>

                    {/* Auto-calculated totals */}
                    <div className="border-t pt-3 space-y-2">
                      {(() => {
                        const tyreFitment = parseFloat(quoteForm.tyre_fitment_cost) || 0;
                        const alignment = parseFloat(quoteForm.alignment_cost) || 0;
                        const rotation = parseFloat(quoteForm.rotation_cost) || 0;
                        const enteredTotal = tyreFitment + alignment + rotation;
                        
                        // Only show calculations if there's a value entered
                        if (enteredTotal <= 0) {
                          return (
                            <p className="text-sm text-muted-foreground text-center py-2">
                              Enter prices above to see the total
                            </p>
                          );
                        }
                        
                        let subtotalExclGst, gst, totalInclGst;
                        if (quoteForm.prices_include_gst) {
                          totalInclGst = enteredTotal;
                          subtotalExclGst = totalInclGst / 1.15;
                          gst = totalInclGst - subtotalExclGst;
                        } else {
                          subtotalExclGst = enteredTotal;
                          gst = subtotalExclGst * 0.15;
                          totalInclGst = subtotalExclGst + gst;
                        }
                        
                        return (
                          <>
                            <div className="flex justify-between text-sm">
                              <span>Subtotal (excl. GST)</span>
                              <span>${subtotalExclGst.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span>GST (15%)</span>
                              <span>${gst.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between font-semibold text-lg border-t pt-2">
                              <span>Total (incl. GST)</span>
                              <span className="text-primary">${totalInclGst.toFixed(2)}</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Earliest Service Date - mandatory */}
                  <div className="space-y-2">
                    <Label>Earliest Service Date <span className="text-destructive">*</span></Label>
                    <DatePicker
                      value={quoteForm.service_date_estimate}
                      onChange={(date) => setQuoteForm({ ...quoteForm, service_date_estimate: date })}
                      placeholder="Select date"
                      minDate={new Date()}
                      required={true}
                    />
                  </div>

                  {/* Quote Expiry - only for tyre services */}
                  {(viewingQuote?.tyre_brand || viewingQuote?.request_info?.service_type?.includes("tyres")) && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Quote Expiry</Label>
                        <div className="flex items-center gap-2">
                          <Label htmlFor="edit-has-expiry" className="text-xs text-muted-foreground">Has Expiry?</Label>
                          <Switch
                            id="edit-has-expiry"
                            checked={quoteForm.has_expiry}
                            onCheckedChange={(checked) => setQuoteForm({ 
                              ...quoteForm, 
                              has_expiry: checked,
                              quote_expiry: checked ? quoteForm.quote_expiry : null
                            })}
                          />
                        </div>
                      </div>
                      {quoteForm.has_expiry ? (
                        <DatePicker
                          value={quoteForm.quote_expiry}
                          onChange={(date) => setQuoteForm({ ...quoteForm, quote_expiry: date })}
                          placeholder="Select expiry date"
                          minDate={new Date()}
                        />
                      ) : (
                        <p className="text-sm text-muted-foreground p-2 bg-muted/30 rounded">
                          No expiry - quote remains valid until accepted
                        </p>
                      )}
                    </div>
                  )}

                  {/* Notes */}
                  <div className="space-y-2">
                    <Label>Notes / Conditions</Label>
                    <Textarea
                      placeholder="Any additional information or conditions..."
                      value={quoteForm.notes}
                      onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })}
                      rows={3}
                    />
                  </div>
                </div>
              ) : (
                // View Mode - shows full price breakdown
                <div className="space-y-4">
                  {/* Tyre Details - only show if applicable */}
                  {(viewingQuote.tyre_brand || viewingQuote.tyre_model) && (
                    <div className="space-y-2">
                      <p className="font-heading font-semibold text-lg">
                        {viewingQuote.tyre_brand} {viewingQuote.tyre_model}
                      </p>
                      {viewingQuote.tyre_size && (
                        <p className="text-muted-foreground">
                          {viewingQuote.tyre_size}{viewingQuote.speed_rating ? ` – ${viewingQuote.speed_rating}` : ""}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Price Display - Full Breakdown */}
                  <div className="p-4 bg-primary/5 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">Pricing</p>
                      {viewingQuote.price_breakdown?.prices_include_gst !== undefined && (
                        <Badge variant="outline" className="text-xs">
                          {viewingQuote.price_breakdown.prices_include_gst ? "Prices incl. GST" : "Prices excl. GST"}
                        </Badge>
                      )}
                    </div>
                    
                    {viewingQuote.price_breakdown && (
                      <div className="space-y-2 text-sm">
                        {(viewingQuote.price_breakdown.tyre_fitment > 0 || viewingQuote.price_breakdown.tyres > 0) && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Tyre & Fitment</span>
                            <span>${(viewingQuote.price_breakdown.tyre_fitment || viewingQuote.price_breakdown.tyres || 0).toFixed(2)}</span>
                          </div>
                        )}
                        {viewingQuote.price_breakdown.alignment > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Wheel Alignment</span>
                            <span>${viewingQuote.price_breakdown.alignment.toFixed(2)}</span>
                          </div>
                        )}
                        {viewingQuote.price_breakdown.rotation > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Tyre Rotation</span>
                            <span>${viewingQuote.price_breakdown.rotation.toFixed(2)}</span>
                          </div>
                        )}
                        {viewingQuote.price_breakdown.subtotal_excl_gst && (
                          <>
                            <div className="flex justify-between border-t pt-2">
                              <span className="text-muted-foreground">Subtotal (excl. GST)</span>
                              <span>${viewingQuote.price_breakdown.subtotal_excl_gst.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">GST (15%)</span>
                              <span>${viewingQuote.price_breakdown.gst.toFixed(2)}</span>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    
                    <div className="flex justify-between font-semibold text-lg border-t pt-2">
                      <span>Total (incl. GST)</span>
                      <span className="text-primary">${(viewingQuote.total_price || 0).toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Service Date */}
                  <div>
                    <p className="text-sm text-muted-foreground">Earliest Service Date</p>
                    <p className="font-medium">
                      {(viewingQuote.fitment_estimate || viewingQuote.service_date_estimate)
                        ? formatNZDate(viewingQuote.fitment_estimate || viewingQuote.service_date_estimate)
                        : "To be confirmed"}
                    </p>
                  </div>

                  {/* Quote Expiry */}
                  <div>
                    <p className="text-sm text-muted-foreground">Quote Expiry</p>
                    <p className="font-medium">
                      {viewingQuote.quote_expiry 
                        ? formatNZDate(viewingQuote.quote_expiry)
                        : "No expiry - valid until accepted"}
                    </p>
                  </div>

                  {/* Notes */}
                  {viewingQuote.notes && (
                    <div>
                      <p className="text-sm text-muted-foreground">Notes / Conditions</p>
                      <p className="font-medium">{viewingQuote.notes}</p>
                    </div>
                  )}

                  {/* Submitted Date */}
                  <div className="text-sm text-muted-foreground">
                    Submitted: {formatNZDateTime(viewingQuote.created_at)}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            {editQuoteMode ? (
              <>
                <Button variant="outline" onClick={() => setEditQuoteMode(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveQuoteEdit}
                  disabled={savingQuoteEdit}
                  className="bg-primary"
                >
                  {savingQuoteEdit ? "Saving..." : "Save Changes"}
                </Button>
              </>
            ) : (
              <>
                {/* Within edit window: show Edit + Delete buttons */}
                {viewingQuote && isQuoteEditable(viewingQuote.created_at) && !viewingQuote.deletion_status && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => setEditQuoteMode(true)}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Quote
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleDeleteQuote(viewingQuote.quote_id)}
                      disabled={deletingQuote}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {deletingQuote ? "Deleting..." : "Delete"}
                    </Button>
                  </>
                )}
                {/* After edit window: show Request Deletion */}
                {viewingQuote && !isQuoteEditable(viewingQuote.created_at) && !viewingQuote.deletion_status && (
                  <Button
                    variant="destructive"
                    onClick={() => onRequestDeletion(viewingQuote.quote_id)}
                    data-testid="request-deletion-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Request Deletion
                  </Button>
                )}
                <Button variant="outline" onClick={() => setViewQuoteDialogOpen(false)}>
                  Close
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Availability Dialog */}
    </>
  );
}
